import _ from 'lodash';
import {ProtocolExplorerAccordionGroup, ProtocolExplorerFilterType} from 'constants/protocolExplorerEnum';
import {ExplorerContentTypes, ExplorerViewMode} from '../constants/dataEnum';
import {parseMessage} from './stringHelper';

const DefaultKeyPress = {ctrlKeyPressed: false, shiftKeyPressed: false};

export const getExplorerSelectionData = (
  currentItemList,
  selectedItems,
  lastSelectedItemIndex,
  keyPress = DefaultKeyPress,
) => {
  const {ctrlKeyPressed, shiftKeyPressed} = keyPress;

  const currentSelectedItems = currentItemList.filter((item) => item.isSelected);
  const itemsShouldBeSelected = selectedItems.filter((item) => item.show);
  const clickedItemIDs = selectedItems.map((item) => item.ID);

  let clickedIndex = null;

  for (let i = 0, ii = currentItemList.length; i < ii; i += 1) {
    if (!clickedItemIDs.includes(currentItemList[i].ID)) {
      currentItemList[i].isSelected = false;
      continue;
    }

    if (clickedIndex === null) {
      clickedIndex = i;
    }

    clickedIndex = clickedIndex < i ? clickedIndex : i;
  }

  clickedIndex = clickedIndex ?? -1;

  let toBeSelectedIds = [];

  if (itemsShouldBeSelected.length === 1) {
    if (shiftKeyPressed && lastSelectedItemIndex !== -1) {
      for (
        let index = lastSelectedItemIndex;
        clickedIndex > lastSelectedItemIndex ? index !== clickedIndex + 1 : index !== clickedIndex - 1;
        clickedIndex > lastSelectedItemIndex ? (index += 1) : (index -= 1)
      ) {
        const currItem = currentItemList[index];
        toBeSelectedIds.push(currItem.ID);
      }
    } else if (ctrlKeyPressed && lastSelectedItemIndex !== -1) {
      const toggledOffIds = itemsShouldBeSelected.map((item) => item.isSelected && item.ID);
      toBeSelectedIds = itemsShouldBeSelected.map((item) => !item.isSelected && item.ID);

      const additionalSelectedItems = currentSelectedItems.filter((e) => !toggledOffIds.find((ID) => ID === e.ID));
      const additionalSelectedItemIds = additionalSelectedItems.map((e) => e.ID);

      toBeSelectedIds = [...additionalSelectedItemIds, ...toBeSelectedIds];
    } else {
      // eslint-disable-next-line no-lonely-if
      if (currentSelectedItems.length === 1) {
        toBeSelectedIds = itemsShouldBeSelected.map((item) => !item.isSelected && item.ID);
      } else {
        toBeSelectedIds = itemsShouldBeSelected.map((item) => item.ID);
      }
    }
  } else {
    toBeSelectedIds = itemsShouldBeSelected.map((item) => item.ID);

    if (shiftKeyPressed) {
      const additionalSelectedItems = currentSelectedItems.filter((e) => !toBeSelectedIds.includes(e.ID));
      const additionalSelectedItemIds = additionalSelectedItems.map((e) => e.ID);

      toBeSelectedIds = [...toBeSelectedIds, ...additionalSelectedItemIds];
    } else if (ctrlKeyPressed) {
      const toggledOffIds = itemsShouldBeSelected.map((item) => item.isSelected && item.ID);
      toBeSelectedIds = itemsShouldBeSelected.map((item) => !item.isSelected && item.ID);

      const additionalSelectedItems = currentSelectedItems.filter((e) => !toggledOffIds.find((ID) => ID === e.ID));
      const additionalSelectedItemIds = additionalSelectedItems.map((e) => e.ID);

      toBeSelectedIds = [...additionalSelectedItemIds, ...toBeSelectedIds];
    }
  }

  return {toBeSelectedIds, clickedIndex};
};

function getItemTypeFileName(items) {
  if (items.every((e) => e.type === ExplorerContentTypes.FOLDER)) {
    return 'folders';
  }

  if (items.every((e) => e.type === ExplorerContentTypes.PROTOCOL)) {
    return 'protocols';
  }

  if (
    [ExplorerContentTypes.FOLDER, ExplorerContentTypes.PROTOCOL].every((currentType) =>
      items.some((e) => e.type === currentType),
    )
  ) {
    return 'folder(s)_and_protocol(s)';
  }

  return '';
}

export function generateExportedItemsFileName(exportedItems, maxLength) {
  let fileName = '';

  if (exportedItems.length > 1) {
    const datetime = new Date();

    const year = datetime.getFullYear();
    let month = datetime.getMonth();
    let date = datetime.getDate();
    let hour = datetime.getHours();
    let minute = datetime.getMinutes();
    let second = datetime.getSeconds();

    month = month < 10 ? `0${month}` : month;
    date = date < 10 ? `0${date}` : date;
    hour = hour < 10 ? `0${hour}` : hour;
    minute = minute < 10 ? `0${minute}` : minute;
    second = second < 10 ? `0${second}` : second;

    const fullDate = `${year}${month}${date}`;
    const time = `${hour}${minute}${second}`;

    const itemTypeFileName = getItemTypeFileName(exportedItems);

    fileName = `${itemTypeFileName}_${fullDate}_${time}`;
  } else {
    fileName = exportedItems[0].name || '';

    if (maxLength && fileName.length > maxLength) {
      fileName = fileName.substring(0, maxLength);
    }
  }

  return fileName;
}

export function createDownloadFile(data, fileName, extension) {
  const url = window.URL.createObjectURL(new Blob([data]));
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', `${fileName}${extension}`);
  document.body.appendChild(link);
  link.click();
}

export function getProtocolIndexFromList(protocolList, protocolId) {
  const sortedProtocolList = _.orderBy([...protocolList], ['type']);
  const index = sortedProtocolList.findIndex((e) => e.ID === protocolId);

  return index;
}

export function getProtocolIcon(protocol, isOperatorRoleLoggedIn, isProtocolOwner) {
  if (protocol.type === ExplorerContentTypes.FOLDER) {
    if (!isProtocolOwner && !protocol[protocol.type].isTessaraProtocol) {
      return 'FastSharedFolderOtherUser';
    }
    if (protocol[protocol.type].sharedTo.length > 0 && !isOperatorRoleLoggedIn) {
      return 'FastFolderShared';
    }
    return 'Folder';
  }
  if (!isProtocolOwner && !protocol[protocol.type].isTessaraProtocol) {
    return 'FastSharedProtocolOtherUser';
  }
  if (protocol[protocol.type].sharedTo.length > 0 && !isOperatorRoleLoggedIn) {
    return 'FastProtocolShared';
  }
  return 'Protocol';
}

// for context menu
export const getExplorerItemDataAttribute = (item, index) => `${item.ID}/${index}/${item?.isSelected}`;
export const convertExplorerItemDataAttributeToArray = (dataInString = '') => dataInString.split('/');
export const PROTOCOL_EXPLORER_ATTRIBUTE_IDENTIFIER = 'data-id';

export const EXPLORER_ITEM_CLASSNAME = 'protocol-explorer-item';
export const EXPLORER_ITEM_CLASSNAME_DETAIL_VIEW = 'protocol-explorer-item-detail-view';

export const extractProtocolExplorerContextMenuData = (clickedElement, explorerViewMode) => {
  let elementWithDataAttribute = clickedElement;

  // The detail view have special condition because we cannot access and modify the table's row directly.
  // TODO: migrate detail view's table to FmlxCompoundTable to be able to insert data attribute into row element.
  if (explorerViewMode === ExplorerViewMode.DETAIL) {
    elementWithDataAttribute = clickedElement.firstChild.firstChild;
  }

  const dataAttributeInString = elementWithDataAttribute?.attributes[PROTOCOL_EXPLORER_ATTRIBUTE_IDENTIFIER]?.value;
  const [protocolId, itemIndex, isSelected] = convertExplorerItemDataAttributeToArray(dataAttributeInString);

  return {protocolId, itemIndex, isSelected};
};

// #endregion for context menu

export const isProtocolOwner = (protocol, isOperatorRoleLoggedIn, userId) =>
  protocol[protocol.type].user.userId === userId;

export const getProtocolExplorerItemOwner = (item) => item[item.type].user;

const shapeToExplorerItem = (item, itemType, groupView) => {
  const {id, name, isFavorite, lastModifiedTime, createdTime} = item;

  return {
    ID: id,
    isSelected: false,
    name: name?.toString(),
    show: true,
    type: itemType,
    groupView,
    modifiedString: lastModifiedTime,
    createdString: createdTime,
    isFavorite: isFavorite || false,
    [itemType]: item,
  };
};

const formatFavoriteProtocols = (payload = []) => {
  const formattedPayload = [];

  payload.forEach((protocol) => {
    const itemType = ExplorerContentTypes.PROTOCOL;
    const groupView = ProtocolExplorerAccordionGroup.FAVORITE_PROTOCOLS;

    const flaggedFavoriteProtocol = {...protocol, isFavorite: true};

    const formattedProtocol = shapeToExplorerItem(flaggedFavoriteProtocol, itemType, groupView);
    formattedPayload.push({...formattedProtocol, ID: `fav-${protocol.id}`});
  });

  return formattedPayload;
};

const generateAccordionItems = ({
  protocols = {count: 0, content: null},
  folders = {count: 0, content: null},
  favoriteProtocols = {count: 0, content: null},
}) => {
  const items = [];

  if (favoriteProtocols.count) {
    const {count, content} = favoriteProtocols;
    const title = parseMessage('explorer.content.itemGroups.favoriteProtocols', {count});

    items.push({title, content});
  }

  if (folders.count) {
    const {count, content} = folders;
    const title = parseMessage('explorer.content.itemGroups.folders', {count});

    items.push({title, content});
  }

  if (protocols.count) {
    const {count, content} = protocols;
    const title = parseMessage('explorer.content.itemGroups.protocols', {count});

    items.push({title, content});
  }

  return items;
};

const isContainingTessaraProtocol = (protocols) => {
  const tessaraProtocol = protocols.find((p) => Boolean(p?.protocol?.isTessaraProtocol));

  return Boolean(tessaraProtocol);
};

const isOwnedEveryItems = (protocols, userId) => protocols.every((p) => p?.[p.type]?.owner === userId);

const FilterOptionDefaultParameter = {
  type: ProtocolExplorerFilterType.ALL_ITEMS,
  value: null,
};

const filterItems = (items, filterOption = FilterOptionDefaultParameter, trayProtocolIdsList = []) => {
  const {type: filterType, value: filterValue} = filterOption;

  const filteredItems = items.map((item) => {
    const rawItemData = item[item.type];
    let isPassedFilter = true;

    if (filterType === ProtocolExplorerFilterType.NAME) {
      isPassedFilter =
        item.name
          .toLowerCase()
          // eslint-disable-next-line prefer-regex-literals
          .replace(new RegExp('(\\b.fast\\b)(?!.*\\b\\1\\b)', 'i'), '')
          .indexOf(filterValue.toLowerCase()) !== -1;
    }

    if (filterType === ProtocolExplorerFilterType.TESSARA_PROTOCOLS) {
      isPassedFilter = rawItemData?.isTessaraProtocol && item.type !== ExplorerContentTypes.FOLDER;
    }

    if (filterType === ProtocolExplorerFilterType.TRAY_TYPE) {
      isPassedFilter = rawItemData?.trayType === filterValue;
    }

    if (filterType === ProtocolExplorerFilterType.SAVED_IN_TRAY) {
      isPassedFilter = trayProtocolIdsList.indexOf(rawItemData.id) !== -1;
    }

    if (filterType === ProtocolExplorerFilterType.SHARED_ITEMS) {
      isPassedFilter = rawItemData.sharedTo.length;
    }

    if (isPassedFilter) {
      return Object.assign(item, {show: true});
    }

    return Object.assign(item, {show: false});
  });

  return filteredItems;
};

const sortItems = (items, reverse, accessor = 'name') => {
  const sortedItems = [...items];

  sortedItems.sort((a, b) => a[accessor].localeCompare(b[accessor], undefined, {numeric: true, sensitivity: 'base'}));

  return reverse ? sortedItems.reverse() : sortedItems;
};

const formatDateString = (dateString) => {
  if (!dateString) return '-';
  try {
    return new Date(`${dateString}Z`).toLocaleString();
  } catch {
    return '-';
  }
};

const checkIsItemDraggable = (protocol, userId) => {
  const isOwner = isOwnedEveryItems([protocol], userId);
  const isOnFavoriteGroup = protocol.groupView === ProtocolExplorerAccordionGroup.FAVORITE_PROTOCOLS;
  const isTessaraProtocol = protocol?.protocol?.isTessaraProtocol;

  return (isOwner || isTessaraProtocol) && !isOnFavoriteGroup;
};

const handleAccordionClickAndGetExpandStatus = (
  expandedIndex,
  newExpandStatus,
  {protocols, folders, favoriteProtocols},
) => {
  const currentlyExpandedItemsOrder = _.compact([
    favoriteProtocols.length ? ProtocolExplorerAccordionGroup.FAVORITE_PROTOCOLS : null,
    folders.length ? ProtocolExplorerAccordionGroup.FOLDERS : null,
    protocols.length ? ProtocolExplorerAccordionGroup.PROTOCOLS : null,
  ]);

  const groupNewExpandStatus = {[currentlyExpandedItemsOrder[expandedIndex]]: newExpandStatus};
  return groupNewExpandStatus;
};

const getItemPath = (lineage) => {
  const ancestors = lineage.map((folder) => folder.name);
  ancestors.unshift('root');

  const path = ancestors.join('/');

  return path;
};

const getSelectedItemsUniformType = (selectedItems) => {
  let allItemsType;

  const isMixed = selectedItems.find((item) => {
    const isMixedType = item.type !== undefined && item.type !== allItemsType;

    allItemsType = item.type;
    return isMixedType;
  });

  return isMixed ? ExplorerContentTypes.ITEM : allItemsType;
};

export const protocolExplorerHelper = {
  generateAccordionItems,
  isContainingTessaraProtocol,
  isOwnedEveryItems,
  formatFavoriteProtocols,
  shapeToExplorerItem,
  filterItems,
  sortItems,
  formatDateString,
  checkIsItemDraggable,
  handleAccordionClickAndGetExpandStatus,
  getItemPath,
  getSelectedItemsUniformType,
};
